import React, { useState, useRef } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ImagePlus, StickyNote, Trash2, Camera } from "lucide-react";

export default function SouvenirsBoard({ trip, onUpdate }) {
  const { user } = useAuth();
  const isPro = user?.plan === "pro";
  const [noteText, setNoteText] = useState("");
  const [noteCaption, setNoteCaption] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [imgUrls, setImgUrls] = useState({});
  const items = (trip.souvenirs || []).filter(s => !s.is_deleted).slice().reverse();
  const photoCount = items.filter(s => s.type === "photo").length;
  const atFreeCap = !isPro && photoCount >= 5;

  // Revoke blob URLs on unmount to avoid tab memory growth
  const imgUrlsRef = useRef(imgUrls);
  imgUrlsRef.current = imgUrls;
  React.useEffect(() => () => {
    Object.values(imgUrlsRef.current).forEach(u => URL.revokeObjectURL(u));
  }, []);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8 MB"); return; }
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    form.append("caption", caption);
    try {
      await api.post(`/trips/${trip.trip_id}/souvenirs/photo`, form, { headers: { "Content-Type": "multipart/form-data" }});
      const { data } = await api.get(`/trips/${trip.trip_id}`);
      onUpdate(data); setCaption(""); e.target.value = "";
      toast.success("Souvenir added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/trips/${trip.trip_id}/souvenirs/note`, { text: noteText, caption: noteCaption });
      const { data } = await api.get(`/trips/${trip.trip_id}`);
      onUpdate(data); setNoteText(""); setNoteCaption("");
      toast.success("Note pinned");
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this souvenir?")) return;
    try {
      await api.delete(`/trips/${trip.trip_id}/souvenirs/${id}`);
      const { data } = await api.get(`/trips/${trip.trip_id}`);
      onUpdate(data);
    } catch { toast.error("Failed"); }
  };

  const loadImage = async (sv) => {
    if (imgUrls[sv.id]) return;
    try {
      const resp = await api.get(`/trips/${trip.trip_id}/souvenirs/${sv.id}/download`, { responseType: "blob" });
      setImgUrls(u => ({ ...u, [sv.id]: URL.createObjectURL(resp.data) }));
    } catch {}
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif-display text-2xl font-semibold">Souvenirs</h2>
        <p className="text-sm text-stone-500">Return home and turn this itinerary into a keepsake — pin photos and memories.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
          <div className="font-mono-code text-[10px] uppercase tracking-widest text-amber-700 flex items-center gap-1"><Camera size={12}/> Add a photo</div>
          <Input value={caption} onChange={(e)=>setCaption(e.target.value)} placeholder="Caption (optional)" className="mt-3" data-testid="sv-photo-caption"/>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" onChange={uploadPhoto} className="hidden" data-testid="sv-photo-input"/>
          <Button onClick={()=>fileRef.current?.click()} disabled={busy || atFreeCap} className="mt-3 w-full bg-[#C85A32] hover:bg-[#B04B26]" data-testid="sv-photo-button">
            <ImagePlus size={14} className="mr-1"/> {busy ? "Uploading…" : "Choose photo"}
          </Button>
          {!isPro && (
            <div className="mt-2 text-[11px] text-stone-500" data-testid="sv-photo-limit-hint">
              {photoCount}/5 free photos used{atFreeCap && <> — <a href="/upgrade" className="text-[#C85A32] underline" data-testid="sv-photo-upgrade-link">go Pro for unlimited</a></>}
            </div>
          )}
        </div>

        <form onSubmit={addNote} className="rounded-2xl border border-stone-200 bg-white p-5">
          <div className="font-mono-code text-[10px] uppercase tracking-widest text-amber-700 flex items-center gap-1"><StickyNote size={12}/> Pin a memory</div>
          <Textarea value={noteText} onChange={(e)=>setNoteText(e.target.value)} placeholder="The little ramen shop tucked behind the shrine…" className="mt-3 min-h-24" data-testid="sv-note-text"/>
          <Input value={noteCaption} onChange={(e)=>setNoteCaption(e.target.value)} placeholder="Optional caption" className="mt-2" data-testid="sv-note-caption"/>
          <Button type="submit" disabled={busy || !noteText.trim()} className="mt-3 w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="sv-note-button">
            {busy ? "Saving…" : "Pin memory"}
          </Button>
        </form>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-stone-300 rounded-2xl bg-white text-stone-500">
          <StickyNote size={22} className="mx-auto mb-2 text-stone-400"/> No souvenirs yet. Add one after your trip.
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
          {items.map(sv => (
            <div key={sv.id} className="mb-4 break-inside-avoid rounded-2xl overflow-hidden border border-stone-200 bg-white group relative" data-testid={`sv-${sv.id}`}>
              {sv.type === "photo" ? (
                <PhotoTile sv={sv} imgUrls={imgUrls} loadImage={loadImage}/>
              ) : (
                <div className="p-5">
                  <div className="font-serif-display text-lg leading-snug text-stone-900">{sv.text}</div>
                  {sv.caption && <div className="font-mono-code text-[10px] uppercase tracking-widest text-amber-700 mt-2">{sv.caption}</div>}
                </div>
              )}
              <div className="px-4 py-2 text-[10px] font-mono-code text-stone-500 flex items-center justify-between border-t border-stone-100">
                <span>{(sv.created_at || "").slice(0,10)}</span>
                <button onClick={()=>remove(sv.id)} data-testid={`sv-delete-${sv.id}`} className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600 transition"><Trash2 size={13}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoTile({ sv, imgUrls, loadImage }) {
  React.useEffect(() => { loadImage(sv); /* eslint-disable-next-line */ }, [sv.id]);
  const src = imgUrls[sv.id];
  return (
    <div>
      {src ? (
        <img src={src} alt={sv.caption} className="w-full block"/>
      ) : (
        <div className="w-full aspect-[4/3] bg-stone-100 animate-pulse"/>
      )}
      {sv.caption && <div className="p-3 font-serif-display text-sm text-stone-700 italic">{sv.caption}</div>}
    </div>
  );
}
